/**
 * Decode React's minified production error messages into their full text.
 *
 * Production React is always minified, so a render crash surfaces as e.g.
 *   "Minified React error #130; visit
 *    https://react.dev/errors/130?args[]=undefined&args[]= for the full message …"
 * which says nothing about what actually broke. React ships the real message
 * templates in `scripts/error-codes/codes.json` (≈590 entries, ~66KB), but
 * vendoring the whole map is overkill — in practice only a handful of codes
 * ever reach a user. This module keeps a CURATED table of the high-value codes
 * (copied verbatim from React 19's canonical codes.json) plus a general decoder
 * that:
 *   1. detects the "Minified React error #N" signature,
 *   2. pulls the `args[]` values React embedded in the react.dev URL,
 *   3. interpolates them into the template's `%s` slots in encounter order.
 *
 * For any code NOT in the curated table it returns `null` — the caller then
 * falls back to React's original message, which still carries the
 * `react.dev/errors/N` link, so information is never lost.
 *
 * Pure + side-effect free so it can be unit-tested and called from both the
 * functional route boundary (`RouteErrorBoundary`) and the class diagnostic
 * boundary (`DiagnosticErrorBoundary`). Whether to surface the decoded text is
 * the caller's call — `RouteErrorBoundary` gates console output to non-prod
 * stages (see `isProdEnvironment`), pairing with the source maps BRE-4414 /
 * BRE-4418 enable on staging + dev.
 *
 * BRE-4418. To add a code: copy its template verbatim from
 * https://raw.githubusercontent.com/facebook/react/main/scripts/error-codes/codes.json
 */

/**
 * Curated React 19 production error-code → message templates, verbatim from
 * the canonical codes.json. `%s` placeholders are filled, in order, from the
 * `args[]` React embeds in the minified message. Keys are strings because they
 * come from a regex capture group. Add the codes you actually hit; rare codes
 * fall through to React's original message (which still links react.dev).
 */
const REACT_ERROR_TEMPLATES: Readonly<Record<string, string>> = {
  // Bad/missing import or default-vs-named export mismatch — the BRE-4413
  // /agent crash. `%s.%s` = the resolved value (e.g. "undefined") + an optional
  // suffix React appends naming the parent component.
  "31": "Objects are not valid as a React child (found: %s). If you meant to render a collection of children, use an array instead.",
  "130": "Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: %s.%s",
  "152": "Nothing was returned from render. This usually means a return statement is missing. Or, to render nothing, return null.",
  "185": "Maximum update depth exceeded. This can happen when a component repeatedly calls setState inside componentWillUpdate or componentDidUpdate. React limits the number of nested updates to prevent infinite loops.",
  "300": "Rendered fewer hooks than expected. This may be caused by an accidental early return statement.",
  "301": "Too many re-renders. React limits the number of renders to prevent an infinite loop.",
  "310": "Rendered more hooks than during the previous render.",
  "321": "Invalid hook call. Hooks can only be called inside of the body of a function component. This could happen for one of the following reasons:\n1. You might have mismatching versions of React and the renderer (such as React DOM)\n2. You might be breaking the Rules of Hooks\n3. You might have more than one copy of React in the same app\nSee https://react.dev/link/invalid-hook-call for tips about how to debug and fix this problem.",
  "418": "Hydration failed because the server rendered %s didn't match the client. As a result this tree will be regenerated on the client. This can happen if a SSR-ed Client Component used:\n\n- A server/client branch `if (typeof window !== 'undefined')`.\n- Variable input such as `Date.now()` or `Math.random()` which changes each time it's called.\n- Date formatting in a user's locale which doesn't match the server.\n- External changing data without sending a snapshot of it along with the HTML.\n- Invalid HTML tag nesting.\n\nIt can also happen if the client has a browser extension installed which messes with the HTML before React loaded.\n\nhttps://react.dev/link/hydration-mismatch%s",
  "419": "The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering.",
  "421": "This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering. The usual way to fix this is to wrap the original update in startTransition.",
  "422": "There was an error while hydrating but React was able to recover by instead client rendering from the nearest Suspense boundary.",
  "423": "There was an error while hydrating but React was able to recover by instead client rendering the entire root.",
  "425": "Text content does not match server-rendered HTML.",
  "426": "A component suspended while responding to synchronous input. This will cause the UI to be replaced with a loading indicator. To fix, updates that suspend should be wrapped with startTransition.",
  "482": "An unknown Component is an async Client Component. Only Server Components can be async at the moment. This error is often caused by accidentally adding `'use client'` to a module that was originally written for the server.",
};

/** Minimal shape we read off a caught error without depending on `Error`. */
interface ErrorLike {
  message?: string | null;
  stack?: string | null;
}

// "Minified React error #130" — the code is the only capture we need; the
// react.dev URL (with the args) lives in the same string and is parsed below.
const MINIFIED_REACT_ERROR_RE = /Minified React error #(\d+)/;

// React appends the original args as repeated `args[]=<urlencoded>` query
// params on the react.dev URL it embeds in the message.
const ARGS_RE = /[?&]args\[\]=([^&\s"']*)/g;

/** Build the searchable haystack from a string or an error-like object. */
function toHaystack(input: string | ErrorLike | null | undefined): string {
  if (typeof input === "string") return input;
  if (input && typeof input === "object") {
    return [input.message, input.stack]
      .filter((s): s is string => typeof s === "string")
      .join("\n");
  }
  return "";
}

/** Pull the `args[]` values React embedded in the message, in order. */
function extractReactErrorArgs(haystack: string): string[] {
  const args: string[] = [];
  // Regex carries the /g flag, so reset lastIndex defensively in case a prior
  // partial exec left it advanced (this module reuses the literal).
  ARGS_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ARGS_RE.exec(haystack)) !== null) {
    const raw = match[1];
    try {
      args.push(decodeURIComponent(raw));
    } catch {
      // A malformed % sequence makes decodeURIComponent throw — keep the raw
      // value rather than dropping the slot.
      args.push(raw);
    }
  }
  return args;
}

/** Fill `%s` slots in encounter order; leave extras as `%s` if args run out. */
function interpolateTemplate(template: string, args: string[]): string {
  let i = 0;
  return template.replace(/%s/g, () => (i < args.length ? args[i++] : "%s"));
}

/**
 * Decode a minified React production error into its full message.
 *
 * @param input The caught error (or its message string).
 * @returns The decoded message prefixed with `React error #N: …`, or `null`
 *   when the input isn't a minified React error or the code isn't curated (the
 *   caller should then keep React's original message).
 */
export function decodeReactError(
  input: string | ErrorLike | null | undefined,
): string | null {
  const haystack = toHaystack(input);
  if (!haystack) return null;

  const match = MINIFIED_REACT_ERROR_RE.exec(haystack);
  if (!match) return null;

  const code = match[1];
  const template = REACT_ERROR_TEMPLATES[code];
  if (!template) return null;

  const decoded = interpolateTemplate(template, extractReactErrorArgs(haystack));
  return `React error #${code}: ${decoded}`;
}
